import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Eye, Heart, MessageCircle, Share2, Users, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { analyticsService, AnalyticsData, AnalyticsSummary } from '../services/analyticsService';

const AnalyticsView: React.FC = () => {
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [comparisonData, setComparisonData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [period, user]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [data, summaryData, comparison] = await Promise.all([
        analyticsService.getUserAnalytics({ period }),
        analyticsService.getAnalyticsSummary(period),
        analyticsService.getComparisonData(period)
      ]);

      setAnalyticsData(data);
      setSummary(summaryData);
      setComparisonData(comparison);
    } catch (err) {
      setError(t('analytics.error.load', 'Error loading analytics'));
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMetricLabel = (metric: keyof AnalyticsData) => {
    switch (metric) {
      case 'profile_views':
        return t('analytics.metric.profileViews', 'Profile views');
      case 'post_views':
        return t('analytics.metric.postViews', 'Post views');
      case 'post_likes':
        return t('analytics.metric.postLikes', 'Post likes');
      case 'post_comments':
        return t('analytics.metric.postComments', 'Post comments');
      case 'shares':
        return t('analytics.metric.shares', 'Shares');
      case 'followers':
        return t('analytics.metric.followers', 'Followers');
      default:
        return metric.replace('_', ' ');
    }
  };

  const periodOptions = [
    { value: 'week', label: t('analytics.period.week', 'Last week') },
    { value: 'month', label: t('analytics.period.month', 'Last month') },
    { value: 'quarter', label: t('analytics.period.quarter', 'Last quarter') },
    { value: 'year', label: t('analytics.period.year', 'Last year') }
  ] as const;

  const getPeriodRangeLabel = () => {
    switch (period) {
      case 'week':
        return t('analytics.periodRange.week', 'the last week');
      case 'month':
        return t('analytics.periodRange.month', 'the last month');
      case 'quarter':
        return t('analytics.periodRange.quarter', 'the last 3 months');
      case 'year':
        return t('analytics.periodRange.year', 'the last year');
      default:
        return '';
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const MetricCard: React.FC<{
    title: string;
    value: number;
    change?: number;
    icon: React.ElementType;
    color: string;
    bgColor: string;
  }> = ({ title, value, change, icon: Icon, color, bgColor }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-3 rounded-lg ${bgColor}`}>
          <Icon className={color} size={24} />
        </div>
        {change !== undefined && (
          <span className={`text-sm font-medium flex items-center gap-1 ${
            change >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            <TrendingUp size={14} className={change >= 0 ? '' : 'rotate-180'} />
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900">{formatNumber(value)}</h3>
      <p className="text-sm text-gray-600">{title}</p>
    </div>
  );

  const SimpleChart: React.FC<{ data: AnalyticsData[]; metric: keyof AnalyticsData }> = ({ data, metric }) => {
    const values = data.map(d => d[metric] as number);
    const maxValue = Math.max(...values, 1);
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {getMetricLabel(metric)} - {t('analytics.chart.lastDays', `Last {days} days`).replace('{days}', String(data.length))}
        </h3>
        <div className="space-y-2">
          {data.slice(-7).map((day, index) => {
            const value = day[metric] as number;
            const percentage = (value / maxValue) * 100;
            
            return (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16">
                  {new Date(day.date).toLocaleDateString(locale, {
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-3 relative">
                  <div 
                    className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900 w-12 text-right">
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-12 flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" aria-hidden="true"></div>
        <p className="mt-6 text-base font-semibold text-gray-700" role="status" aria-live="polite">
          {t('analytics.loading.title', 'Loading analytics...')}
        </p>
        <p className="mt-2 text-sm text-gray-500">
          {t('analytics.loading.subtitle', 'We are preparing your personalized metrics.')}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="text-purple-600" size={24} />
          {t('navigation.analytics', 'Analytics')}
        </h1>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as typeof period)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {periodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Period Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="text-blue-600" size={16} />
          <span className="text-sm text-blue-700">
            {t('analytics.showingData', 'Showing data from')} {getPeriodRangeLabel()}
          </span>
        </div>
      </div>

      {/* Key Metrics */}
      {summary && comparisonData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title={t('analytics.metric.profileViews', 'Profile views')}
            value={summary.total_profile_views}
            change={comparisonData.changes.profile_views}
            icon={Eye}
            color="text-blue-600"
            bgColor="bg-blue-100"
          />
          <MetricCard
            title={t('analytics.metric.postViews', 'Post views')}
            value={summary.total_post_views}
            change={comparisonData.changes.post_views}
            icon={Eye}
            color="text-green-600"
            bgColor="bg-green-100"
          />
          <MetricCard
            title={t('analytics.summary.totalLikes', 'Total likes')}
            value={summary.total_likes}
            change={comparisonData.changes.post_likes}
            icon={Heart}
            color="text-red-600"
            bgColor="bg-red-100"
          />
          <MetricCard
            title={t('analytics.summary.comments', 'Comments')}
            value={summary.total_comments}
            change={comparisonData.changes.post_comments}
            icon={MessageCircle}
            color="text-purple-600"
            bgColor="bg-purple-100"
          />
        </div>
      )}

      {/* Additional Metrics */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Share2 className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{formatNumber(summary.total_shares)}</h3>
                <p className="text-sm text-gray-600">{t('analytics.summary.shares', 'Shares')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-cyan-100 rounded-lg">
                <Users className="text-cyan-600" size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{summary.net_followers >= 0 ? '+' : ''}{summary.net_followers}</h3>
                <p className="text-sm text-gray-600">{t('analytics.summary.newFollowers', 'New followers')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <TrendingUp className="text-yellow-600" size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">{summary.average_engagement_rate.toFixed(1)}%</h3>
                <p className="text-sm text-gray-600">{t('analytics.summary.engagementRate', 'Engagement rate')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <SimpleChart data={analyticsData} metric="profile_views" />
        <SimpleChart data={analyticsData} metric="post_views" />
        <SimpleChart data={analyticsData} metric="post_likes" />
        <SimpleChart data={analyticsData} metric="post_comments" />
      </div>

      {/* Top Performing Posts */}
      {summary && summary.top_performing_posts.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics.topPosts.title', 'Top performing posts')}</h3>
          <div className="space-y-4">
            {summary.top_performing_posts.map((post, index) => (
              <div key={post.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </div>
                
                {post.media_files && post.media_files[0] && (
                  <img
                    src={post.media_files[0].url}
                    alt={post.title}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}
                
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 line-clamp-1">{post.title}</h4>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                    <span className="flex items-center gap-1">
                      <Eye size={14} />
                      {formatNumber(post.views_count)} {t('analytics.topPosts.views', 'views')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart size={14} />
                      {formatNumber(post.likes_count)} {t('analytics.topPosts.likes', 'likes')}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle size={14} />
                      {formatNumber(post.comments_count)} {t('analytics.topPosts.comments', 'comments')}
                    </span>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="text-sm text-gray-500">
                    {new Date(post.created_at).toLocaleDateString(locale)}
                  </div>
                  <div className="text-sm font-medium text-purple-600">
                    {post.views_count > 0 ? ((post.likes_count + post.comments_count) / post.views_count * 100).toFixed(1) : 0}% {t('analytics.topPosts.engagement', 'engagement')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 {t('analytics.insights.title', 'Insights and recommendations')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {summary && (
            <>
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{t('analytics.insights.bestDay', 'Best day of the week')}</h4>
                <p className="text-sm text-gray-600">
                  {analyticsData.length > 0 && (() => {
                    const dayTotals = analyticsData.reduce((acc: Record<string, number>, day) => {
                      const dayName = new Date(day.date).toLocaleDateString(locale, {
                        weekday: 'long'
                      });
                      acc[dayName] = (acc[dayName] || 0) + day.post_views;
                      return acc;
                    }, {});
                    const bestDay = Object.entries(dayTotals).sort(([, a], [, b]) => b - a)[0];
                    if (!bestDay) return '';
                    const [dayName, views] = bestDay;
                    const viewsLabel = formatNumber(views);
                    return t('analytics.insights.bestDay.detail', `${dayName} with ${viewsLabel} views`)
                      .replace('{day}', dayName)
                      .replace('{views}', viewsLabel);
                  })()}
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{t('analytics.insights.engagement.title', 'Engagement rate')}</h4>
                <p className="text-sm text-gray-600">
                  {summary.average_engagement_rate > 3
                    ? t('analytics.insights.engagement.excellent', 'Excellent! Your content generates a lot of interaction')
                    : summary.average_engagement_rate > 1.5
                    ? t('analytics.insights.engagement.good', 'Good engagement rate, keep it up')
                    : t('analytics.insights.engagement.tip', 'Try creating more interactive content to increase engagement')}
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{t('analytics.insights.growth.title', 'Growth')}</h4>
                <p className="text-sm text-gray-600">
                  {comparisonData && comparisonData.changes.post_views > 0
                    ? t('analytics.insights.growth.positive', 'Your reach grew by {percent}% this period!').replace(
                        '{percent}',
                        comparisonData.changes.post_views.toFixed(1)
                      )
                    : t('analytics.insights.growth.tip', 'Post more often to increase your reach')}
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{t('analytics.insights.opportunity.title', 'Opportunity')}</h4>
                <p className="text-sm text-gray-600">
                  {summary.total_post_views > summary.total_profile_views * 2
                    ? t('analytics.insights.opportunity.bio', 'Your posts attract more than your profile. Optimize your bio.')
                    : t('analytics.insights.opportunity.content', 'Consider creating more content to make the most of interest in your profile.')}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsView;
